import {useState} from "react";
import Modal from "react-bootstrap/Modal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import axiApi from "ApiCaller";
import {SwalAlertClass} from "core/swalClass";
import useCustomTranslation from "core/customTranslation";
import LoaderSpinner from "context/LoaderSpinner";
import {translations} from "./translations";

const IMPORT_URL = "/api/distribution/islands/excel/import/";

const sendFile = (file, confirm) => {
    const body = new FormData();
    body.append("file", file);
    if (confirm) {
        body.append("confirm", "1");
    }
    return axiApi.post(IMPORT_URL, body, {headers: {"Content-Type": "multipart/form-data"}});
};

const ImportSummary = ({summary}) => {
    const {t: _Tc} = useCustomTranslation(translations);
    return (
        <>
            <div className="d-flex gap-4 mt-3">
                <span>{_Tc("summary.news")}: <strong>{summary.news}</strong></span>
                <span>{_Tc("summary.updated")}: <strong>{summary.updated}</strong></span>
                <span className={summary.errors.length ? "text-danger" : ""}>
                    {_Tc("summary.errors")}: <strong>{summary.errors.length}</strong>
                </span>
            </div>
            {summary.errors.length > 0 && (
                <table className="table table-sm mt-3">
                    <thead>
                        <tr>
                            <th>{_Tc("summary.row")}</th>
                            <th>{_Tc("summary.message")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.errors.map((error, index) => (
                            <tr key={index}>
                                <td>{error.row ?? "-"}</td>
                                <td>{error.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </>
    );
};

const ModalImportIslands = ({show, setShow, onImported}) => {
    const {t: _Tc} = useCustomTranslation(translations);
    const Alert = new SwalAlertClass({confirmButtonText: _Tc("button.accept")});
    const [file, setFile] = useState(null);
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const canApply = summary && summary.errors.length === 0 && summary.news + summary.updated > 0;

    const onChangeFile = (e) => {
        setFile(e.target.files[0] || null);
        setSummary(null);
    };

    const onClickValidate = () => {
        if (!file.name.toLowerCase().endsWith(".xlsx")) {
            Alert.warning(_Tc("title"), _Tc("import.not-xlsx"));
            return;
        }
        setIsLoading(true);
        sendFile(file, false)
            .then((resp) => setSummary(resp.data))
            .catch((error) => {
                console.error("Error al validar el Excel de islas:", error);
                Alert.error(_Tc("title"), _Tc("import.error"));
            })
            .finally(() => setIsLoading(false));
    };

    const onClickImport = () => {
        setIsLoading(true);
        sendFile(file, true)
            .then(() => {
                Alert.success(_Tc("title"), _Tc("import.success"));
                onImported();
                setShow(false);
            })
            .catch((error) => {
                // 400: el fichero ya no valida; se muestra el nuevo resumen
                if (error.response?.status === 400 && error.response.data?.errors) {
                    setSummary(error.response.data);
                    return;
                }
                console.error("Error al importar el Excel de islas:", error);
                Alert.error(_Tc("title"), _Tc("import.error"));
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} size="lg">
            <Modal.Header closeButton className="preview-canvas mb-0">
                <div className="preview-icon">
                    <FontAwesomeIcon icon="file-import" />
                </div>
                <div className="preview-title d-inline-flex align-items-center">
                    <Modal.Title className="d-inline-flex ms-1">
                        <div className="h4 ms-0">{_Tc("import.title")}</div>
                    </Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body>
                <LoaderSpinner isLoading={isLoading}>
                    <p className="text-muted">{_Tc("import.help")}</p>
                    <input type="file" accept=".xlsx" className="form-control" onChange={onChangeFile} />
                    {summary && <ImportSummary summary={summary} />}
                </LoaderSpinner>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={() => setShow(false)}>
                    {_Tc("button.cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={!file || isLoading}
                    onClick={onClickValidate}>
                    {_Tc("button.validate")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!canApply || isLoading}
                    onClick={onClickImport}>
                    {_Tc("button.import")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalImportIslands;
